import React from 'react'
import {Pressable, ScrollView, Text, View} from 'react-native'

import {CoinAvatar} from './CoinAvatar'

export type AssetRow = {
  id: string
  symbol: string
  name: string
  imageUrl?: string
  chain: string
  balance: string
  priceUsd?: number
  change24hPct?: number
  valueUsd?: number
}

type Props = {
  assets: AssetRow[]
  isDesktop: boolean
  onPressAsset: (asset: AssetRow) => void
}

const formatUsd = (value?: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  return `$${value.toLocaleString('en-US', {maximumFractionDigits: 2})}`
}

const formatBalance = (value: string, digits = 6) => {
  const parsed = Number.parseFloat(value)
  if (!Number.isFinite(parsed)) return '—'
  return parsed.toLocaleString('en-US', {maximumFractionDigits: digits})
}

const formatPct = (value?: number) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—'
  const pct = value * 100
  return `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`
}

export function AssetTable({assets, isDesktop, onPressAsset}: Props) {
  if (!assets.length) {
    return (
      <View className="rounded-2xl border border-black/10 bg-white/80 p-6 shadow-sm dark:border-white/10 dark:bg-neutral-900/70">
        <Text className="text-sm text-neutral-500 dark:text-neutral-400">
          Aucun asset disponible.
        </Text>
      </View>
    )
  }

  if (!isDesktop) {
    return (
      <View className="gap-3">
        {assets.map(asset => (
          <Pressable
            accessibilityRole="button"
            key={asset.id}
            onPress={() => onPressAsset(asset)}
            className="rounded-2xl border border-black/10 bg-white/80 p-4 shadow-sm active:opacity-90 dark:border-white/10 dark:bg-neutral-900/70">
            <View className="flex-row items-center justify-between">
              <View className="min-w-0 flex-1 flex-row items-center gap-3">
                <CoinAvatar symbol={asset.symbol} imageUrl={asset.imageUrl} size={36} />
                <View className="min-w-0">
                  <Text className="text-sm font-semibold text-neutral-900 dark:text-white">
                    {asset.symbol}
                  </Text>
                  <Text className="text-xs text-neutral-500 dark:text-neutral-400" numberOfLines={1}>
                    {asset.name}
                  </Text>
                </View>
              </View>
              <Text className="text-xs text-neutral-500 dark:text-neutral-400">
                {asset.chain}
              </Text>
            </View>
            <View className="mt-3 flex-row items-center justify-between">
              <View>
                <Text className="text-xs text-neutral-500 dark:text-neutral-400">Balance</Text>
                <Text className="text-sm font-semibold text-neutral-900 dark:text-white">
                  {formatBalance(asset.balance)}
                </Text>
              </View>
              <View className="items-end">
                <Text className="text-xs text-neutral-500 dark:text-neutral-400">Valeur</Text>
                <Text className="text-sm font-semibold text-neutral-900 dark:text-white">
                  {formatUsd(asset.valueUsd)}
                </Text>
                <Text
                  className={`text-xs font-semibold ${
                    typeof asset.change24hPct === 'number'
                      ? asset.change24hPct >= 0
                        ? 'text-emerald-500'
                        : 'text-rose-500'
                      : 'text-neutral-500 dark:text-neutral-400'
                  }`}>
                  {formatPct(asset.change24hPct)}
                </Text>
                <View className="mt-2 rounded-full border border-black/10 px-2.5 py-1 dark:border-white/15">
                  <Text className="text-[11px] text-neutral-700 dark:text-neutral-200">
                    Retirer
                  </Text>
                </View>
              </View>
            </View>
          </Pressable>
        ))}
      </View>
    )
  }

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <View
        style={{minWidth: 920}}
        className="rounded-2xl border border-black/10 bg-white/80 shadow-sm dark:border-white/10 dark:bg-neutral-900/70">
        <View className="flex-row border-b border-black/5 px-4 py-3 dark:border-white/10">
          <Text className="w-56 text-xs uppercase text-neutral-500 dark:text-neutral-400">
            Asset
          </Text>
          <Text className="w-28 text-xs uppercase text-neutral-500 dark:text-neutral-400">
            Reseau
          </Text>
          <Text className="w-28 text-right text-xs uppercase text-neutral-500 dark:text-neutral-400">
            Balance
          </Text>
          <Text className="w-28 text-right text-xs uppercase text-neutral-500 dark:text-neutral-400">
            Prix
          </Text>
          <Text className="w-28 text-right text-xs uppercase text-neutral-500 dark:text-neutral-400">
            Valeur
          </Text>
          <Text className="w-20 text-right text-xs uppercase text-neutral-500 dark:text-neutral-400">
            24h
          </Text>
          <Text className="w-20 text-right text-xs uppercase text-neutral-500 dark:text-neutral-400">
            Action
          </Text>
        </View>
        {assets.map(asset => (
          <Pressable
            accessibilityRole="button"
            key={asset.id}
            onPress={() => onPressAsset(asset)}
            className="flex-row items-center border-b border-black/5 px-4 py-3 active:opacity-95 dark:border-white/10">
            <View className="w-56 flex-row items-center gap-3">
              <CoinAvatar symbol={asset.symbol} imageUrl={asset.imageUrl} size={32} />
              <View className="min-w-0">
                <Text className="text-sm font-semibold text-neutral-900 dark:text-white">
                  {asset.symbol}
                </Text>
                <Text className="text-xs text-neutral-500 dark:text-neutral-400" numberOfLines={1}>
                  {asset.name}
                </Text>
              </View>
            </View>
            <Text className="w-28 text-xs text-neutral-500 dark:text-neutral-400">
              {asset.chain}
            </Text>
            <Text className="w-28 text-right text-sm text-neutral-900 dark:text-white">
              {formatBalance(asset.balance)}
            </Text>
            <Text className="w-28 text-right text-sm text-neutral-900 dark:text-white">
              {formatUsd(asset.priceUsd)}
            </Text>
            <Text className="w-28 text-right text-sm text-neutral-900 dark:text-white">
              {formatUsd(asset.valueUsd)}
            </Text>
            <Text
              className={`w-20 text-right text-sm font-semibold ${
                typeof asset.change24hPct === 'number'
                  ? asset.change24hPct >= 0
                    ? 'text-emerald-500'
                    : 'text-rose-500'
                  : 'text-neutral-500 dark:text-neutral-400'
              }`}>
              {formatPct(asset.change24hPct)}
            </Text>
            <View className="w-20 items-end">
              <View className="rounded-full border border-black/10 px-2.5 py-1 dark:border-white/15">
                <Text className="text-xs text-neutral-700 dark:text-neutral-200">
                  Retirer
                </Text>
              </View>
            </View>
          </Pressable>
        ))}
      </View>
    </ScrollView>
  )
}
